import { Button, CircularProgress, Stack, TableCell, TableRow, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import SyncIcon from "@mui/icons-material/Sync";
import { useState } from "react";

type DataInvalidationRowProps = {
    name: string;
    action: () => Promise<void>;
    actionLabel?: string;
};
export const DataInvalidationRow = (props: DataInvalidationRowProps) => {
    const { name, action, actionLabel } = props;

    const theme = useTheme();
    const { t } = useTranslation();

    const [invalidationLoading, setInvalidationLoading] = useState(false);
    const [invalidationError, setInvalidationError] = useState<string | null>(null);
    const [invalidationSuccess, setInvalidationSuccess] = useState<string | null>(null);

    const invalidateData = async () => {
        setInvalidationLoading(true);
        setInvalidationError(null);

        try {
            await action();

            setInvalidationSuccess(t("settings.data_invalidation.success", "Data invalidated successfuly!"));
        } catch (err) {
            setInvalidationError(err instanceof Error ? err.message : t("common.unknown_error", "Unknown error"));
        } finally {
            setInvalidationLoading(false);
        }
    };

    return (
        <TableRow
            sx={{
                "& .MuiTableCell-root": {
                    borderRight: "1px solid #E7E6E4",
                    padding: "0px 14px 0px 20px",
                    height: "58px",
                    "&:last-child": {
                        borderRight: "none",
                        padding: "0px 8px",
                    },
                },
                "&:hover": {
                    backgroundColor: "#F9F9F8",
                },
            }}
        >
            <TableCell>
                <Stack gap={1}>
                    <Typography variant="small-medium" sx={{ lineHeight: "22px", color: "#1E1E1E", fontWeight: 600 }}>
                        {name}
                    </Typography>
                    {invalidationLoading ?
                        <CircularProgress size={16} />
                    :   <>
                            {invalidationError && (
                                <Typography
                                    variant="small-medium"
                                    sx={{ lineHeight: "22px", fontWeight: 600, color: theme.custom.red[500] }}
                                >
                                    {invalidationError}
                                </Typography>
                            )}
                            {invalidationSuccess && (
                                <Typography
                                    variant="small-medium"
                                    sx={{ lineHeight: "22px", fontWeight: 600, color: theme.custom.green[600] }}
                                >
                                    {invalidationSuccess}
                                </Typography>
                            )}
                        </>
                    }
                </Stack>
            </TableCell>
            <TableCell>
                <Stack direction="row" alignItems="center" sx={{ padding: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<SyncIcon />}
                        onClick={invalidateData}
                        disabled={invalidationLoading}
                        sx={(theme) => ({
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            border: `1px solid ${theme.palette.primary[600]}`,
                            color: `${theme.palette.primary[600]} !important`,
                            backgroundColor: "transparent",
                            "&:hover": {
                                border: `1px solid ${theme.palette.primary[700]}`,
                                color: `${theme.palette.primary[700]} !important`,
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                            "&:disabled": {
                                border: `1px solid ${theme.palette.primary[600]}`,
                                color: `${theme.palette.primary[600]} !important`,
                                opacity: 0.36,
                            },
                        })}
                    >
                        {actionLabel ?? t("settings.data_invalidation.refresh", "Refresh")}
                    </Button>
                </Stack>
            </TableCell>
        </TableRow>
    );
};
